package com.flowyride.feature.settings

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.flowyride.core.domain.favorite.FavoriteException
import com.flowyride.core.domain.place.SavedPlace
import com.flowyride.core.domain.place.SavedPlaceEdit
import com.flowyride.core.domain.place.SavedPlaces
import com.flowyride.core.domain.util.suspendRunCatching
import com.flowyride.feature.settings.resources.Res
import com.flowyride.feature.settings.resources.error_places_load
import com.flowyride.feature.settings.resources.error_places_save
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource

data class SettingsUiState(
    val places: List<SavedPlace> = emptyList(),
    val loadingPlaces: Boolean = true,
    /** Last failure, shown until the next action. */
    val error: StringResource? = null,
    /** A change is being saved: the edit dialog waits. */
    val saving: Boolean = false,
    /** The address being edited (dialog open). */
    val editing: SavedPlace? = null,
)

sealed interface SettingsAction {
    data object Refresh : SettingsAction
    data class Edit(val place: SavedPlace) : SettingsAction
    data object CancelEdit : SettingsAction
    data class Save(val id: Long, val edit: SavedPlaceEdit) : SettingsAction
    data class Delete(val id: Long) : SettingsAction
}

/**
 * Settings of the account. One section per subject; for now the saved addresses, shared with the planner
 * through [SavedPlaces] (a change here shows on its chips and map at once).
 */
class SettingsViewModel(private val savedPlaces: SavedPlaces) : ViewModel() {

    private val _state = MutableStateFlow(SettingsUiState())
    val state: StateFlow<SettingsUiState> = _state.asStateFlow()

    init {
        viewModelScope.launch { savedPlaces.places.collect { places -> _state.update { it.copy(places = places) } } }
        refresh()
    }

    fun onAction(action: SettingsAction) {
        when (action) {
            SettingsAction.Refresh -> refresh()
            is SettingsAction.Edit -> _state.update { it.copy(editing = action.place, error = null) }
            SettingsAction.CancelEdit -> _state.update { it.copy(editing = null) }
            is SettingsAction.Save -> change { savedPlaces.update(action.id, action.edit) }
            is SettingsAction.Delete -> change { savedPlaces.delete(action.id) }
        }
    }

    private fun refresh() {
        _state.update { it.copy(loadingPlaces = true, error = null) }
        viewModelScope.launch {
            val result = suspendRunCatching { savedPlaces.refresh() }
            _state.update { it.copy(loadingPlaces = false, error = if (result.isFailure) Res.string.error_places_load else null) }
        }
    }

    /** Closes the dialog once [block] succeeded; on failure it stays open with the error. */
    private fun change(block: suspend () -> Unit) {
        if (_state.value.saving) return
        _state.update { it.copy(saving = true, error = null) }
        viewModelScope.launch {
            suspendRunCatching { block() }
                .onSuccess { _state.update { it.copy(saving = false, editing = null) } }
                .onFailure { error ->
                    _state.update { it.copy(saving = false, error = Res.string.error_places_save) }
                    // Gone meanwhile (deleted from another device): the list is stale.
                    if (error is FavoriteException.NotFound) {
                        _state.update { it.copy(editing = null) }
                        refresh()
                    }
                }
        }
    }
}
